import React, { useState } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    Grid,
    IconButton,
    Collapse,
    Button,
    InputAdornment,
    Typography,
    Paper
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    Sort as SortIcon
} from '@mui/icons-material';
import { getCategoryStyle } from '../../config';

const AdvancedFilters = ({
    onFilterChange,
    filters,
    configs = {
        search: true,
        dateRange: true,
        type: false,
        members: false,
        categories: false,
        customers: false,
        status: false,
        sortBy: []
    },
    data = {
        members: [],
        categories: [],
        customers: [],
        statuses: []
    }
}) => {
    const [showFilters, setShowFilters] = useState(false);

    const handleChange = (field, value) => {
        onFilterChange({ ...filters, [field]: value });
    };

    const handleClear = () => {
        const cleared = Object.keys(filters).reduce((acc, key) => {
            acc[key] = '';
            return acc;
        }, {});
        onFilterChange(cleared);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 1.5, md: 2 },
                mb: 3,
                border: '1px solid #f1f5f9',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                background: '#fff'
            }}
        >
            <Grid container spacing={2} alignItems="center">
                {/* Main Search */}
                {configs.search && (
                    <Grid item xs={12} md={showFilters ? 12 : 8}>
                        <TextField
                            fullWidth
                            placeholder="Search by keyword..."
                            value={filters.search || ''}
                            onChange={(e) => handleChange('search', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: filters.search && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => handleChange('search', '')}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    bgcolor: '#f8fafc',
                                    '& fieldset': { border: 'none' },
                                    '&:hover fieldset': { border: 'none' },
                                    '&.Mui-focused fieldset': { border: '1px solid #009688' }
                                }
                            }}
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={showFilters ? 12 : 4} sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<FilterIcon />}
                        onClick={() => setShowFilters(!showFilters)}
                        variant={showFilters ? "contained" : "outlined"}
                        fullWidth
                        size="medium"
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
                    >
                        Filters
                    </Button>
                    {Object.values(filters).some(v => v !== '') && (
                        <Button
                            startIcon={<ClearIcon />}
                            onClick={handleClear}
                            color="error"
                            variant="text"
                        >
                            Reset
                        </Button>
                    )}
                </Grid>
            </Grid>

            <Collapse in={showFilters}>
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                    <Grid container spacing={2}>
                        {/* Date Range */}
                        {configs.dateRange && (
                            <>
                                <Grid item xs={6} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Start Date"
                                        type="date"
                                        size="small"
                                        value={filters.startDate || ''}
                                        onChange={(e) => handleChange('startDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <TextField
                                        fullWidth
                                        label="End Date"
                                        type="date"
                                        size="small"
                                        value={filters.endDate || ''}
                                        onChange={(e) => handleChange('endDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Type Filter */}
                        {configs.type && (
                            <Grid item xs={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Type"
                                    size="small"
                                    value={filters.type || ''}
                                    onChange={(e) => handleChange('type', e.target.value)}
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    <MenuItem value="INCOME_SALES">Income</MenuItem>
                                    <MenuItem value="EXPENSE">Expense</MenuItem>
                                    <MenuItem value="PAYMENT">Payment</MenuItem>
                                </TextField>
                            </Grid>
                        )}

                        {/* Categories */}
                        {configs.categories && (
                            <Grid item xs={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Category"
                                    size="small"
                                    value={filters.categoryId || ''}
                                    onChange={(e) => handleChange('categoryId', e.target.value)}
                                >
                                    <MenuItem value="">All Categories</MenuItem>
                                    {data.categories.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id} sx={{ py: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: getCategoryStyle(cat.name).text,
                                                    mr: 1.5
                                                }}
                                            />
                                            {cat.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        {/* Customers */}
                        {configs.customers && (
                            <Grid item xs={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Customer"
                                    size="small"
                                    value={filters.customerId || ''}
                                    onChange={(e) => handleChange('customerId', e.target.value)}
                                >
                                    <MenuItem value="">All Customers</MenuItem>
                                    {data.customers.map(cust => (
                                        <MenuItem key={cust.id} value={cust.id}>{cust.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        {/* Members */}
                        {configs.members && (
                            <Grid item xs={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Member"
                                    size="small"
                                    value={filters.memberId || ''}
                                    onChange={(e) => handleChange('memberId', e.target.value)}
                                >
                                    <MenuItem value="">All Members</MenuItem>
                                    {data.members.map(m => (
                                        <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        {/* Status */}
                        {configs.status && (
                            <Grid item xs={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Status"
                                    size="small"
                                    value={filters.status || ''}
                                    onChange={(e) => handleChange('status', e.target.value)}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    {data.statuses.map(s => (
                                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        {/* Sorting */}
                        {configs.sortBy && configs.sortBy.length > 0 && (
                            <>
                                <Grid item xs={8} md={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Sort By"
                                        size="small"
                                        value={filters.sortBy || ''}
                                        onChange={(e) => handleChange('sortBy', e.target.value)}
                                    >
                                        {configs.sortBy.map(s => (
                                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={4} md={1}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Order"
                                        size="small"
                                        value={filters.sortOrder || 'DESC'}
                                        onChange={(e) => handleChange('sortOrder', e.target.value)}
                                    >
                                        <MenuItem value="DESC">Desc</MenuItem>
                                        <MenuItem value="ASC">Asc</MenuItem>
                                    </TextField>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default AdvancedFilters;
